import React, {FC, ReactNode, useEffect} from 'react';
import PaywallSectionPage from '~Umbraco/Pages/PaywallSectionPage/PaywallSectionPage';
import {
  ICollectionFragmentFragment,
  IPaywallFragmentFragment,
} from '~Api/Graphql/gql/types.generated';
import {usePaywallAccess} from '@src/Providers/PaywallProvider/PaywallProvider';
import useBreakpoint from 'design-system/src/WebComponent/Grid/hooks/useBreakpoint';
import {UmbTypeChecker} from '@src/Umbraco/Utils/UmbTypeChecker';
import {PaywallSectionTrackingProps, PlayerPaywallTrackingProps} from '@src/Services/Amplitude/hooks/tracker';

export type IPaywallParam = ICollectionFragmentFragment['paywall'];
export type IAccessTypParam = ICollectionFragmentFragment['accessType'];

interface PaywallCheckerProps {
  children: ReactNode;
  accessType?: IAccessTypParam;
  paywallId?: string;
  paywall?: IPaywallParam;
  render?: (children: ReactNode) => ReactNode;
  trackingData: PlayerPaywallTrackingProps | PaywallSectionTrackingProps;
}

const VodacomRedirect: FC = () => {
  const {vodacomRedirectToPaywall} = usePaywallAccess();
  useEffect(() => {
    vodacomRedirectToPaywall();
  }, [vodacomRedirectToPaywall]);
  return null;
};

const MtnRedirect: FC = () => {
  const {mtnRedirectToPaywall} = usePaywallAccess();
  useEffect(() => {
    mtnRedirectToPaywall();
  }, [mtnRedirectToPaywall]);
  return null;
};

/**
 * PaywallGate — guard component that conditionally renders either its children
 * or a paywall page, based on the user's access rights and preview settings.
 *
 * For Vodacom carrier users, redirects to Penrose (play.trace.plus) instead of
 * showing the standard Trace paywall. MTN users go to the MTN subscribe page.
 */
const PaywallChecker: FC<PaywallCheckerProps> = ({
  children,
  accessType,
  paywall,
  paywallId: defaultPaywallId,
  render,
  trackingData,
}) => {
  const {canSeeContent, getPremiumPreviewSettings, isVodacomFreeUser, isVodacom, isMtnFreeUser, isMtn} = usePaywallAccess();
  const {isMobile} = useBreakpoint();

  const paywallId: string | undefined =
    defaultPaywallId ??
    (UmbTypeChecker.isPaywall(paywall) ? paywall.id : undefined);

  const hasAccess: boolean = accessType ? canSeeContent(accessType) : true;
  const canPreview = getPremiumPreviewSettings(paywall as IPaywallFragmentFragment)?.canPreview;

  const forceVodacomPaywall =
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('testPaywall') === 'vodacom';

  const shouldDisplayPaywall = forceVodacomPaywall || ((isVodacomFreeUser || isMtnFreeUser) && !hasAccess) || (!hasAccess && !canPreview);

  if (!shouldDisplayPaywall) return <>{children}</>;

  if (isVodacom || forceVodacomPaywall) {
    const vodacomElement = <VodacomRedirect />;
    return <>{render ? render(vodacomElement) : vodacomElement}</>;
  }

  if (isMtn) {
    const mtnElement = <MtnRedirect />;
    return <>{render ? render(mtnElement) : mtnElement}</>;
  }

  const paywallElement = (
    <PaywallSectionPage
      id={paywallId}
      orientation={isMobile ? 'portrait' : 'landscape'}
      trackingData={trackingData}
    />
  );

  return <>{render ? render(paywallElement) : paywallElement}</>;
};

export default PaywallChecker;
